import { getContactDisplayName } from './leadHelpers'
import type { ProjectItem } from './propertyProjectSearch'

export interface OwnerCustomer {
  company_name?: string | null
  contact_first_name?: string | null
  contact_last_name?: string | null
  email?: string | null
  phone?: string | null
  contacts?: Array<{
    first_name?: string
    last_name?: string
    name?: string
    email?: string
    phone?: string
  }> | null
}

export interface OwnerLead {
  company_name?: string | null
  contact_first_name?: string | null
  contact_last_name?: string | null
  email?: string | null
  phone?: string | null
  contacts?: Array<{
    first_name?: string
    last_name?: string
    name?: string
    email?: string
    phone?: string
  }> | null
}

export interface OwnerProperty {
  site_contact_name?: string | null
  site_contact_email?: string | null
  site_contact_phone?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  property_type?: string | null
  name?: string | null
}

export interface ResolvedOwnerContact {
  company_name: string
  contact_name: string
  contact_email: string
  contact_phone: string
  property_address: string
  property_city: string
  property_state: string
  property_zip: string
  property_type: string
}

export interface ResolveOwnerContactInput {
  customer?: OwnerCustomer | null
  lead?: OwnerLead | null
  property?: OwnerProperty | null
  leadProject?: ProjectItem | null
}

function resolveLeadContactName(lead: OwnerLead): string {
  const primaryContact = lead.contacts?.[0]
  if (primaryContact) {
    const fromParts = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
    if (fromParts) return fromParts
    if (primaryContact.name?.trim()) return primaryContact.name.trim()
  }
  return `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim()
}

function resolveLeadContactEmail(lead: OwnerLead): string {
  const primaryContact = lead.contacts?.[0]
  return primaryContact?.email || lead.email || ''
}

function resolveLeadContactPhone(lead: OwnerLead): string {
  const primaryContact = lead.contacts?.[0]
  return primaryContact?.phone || lead.phone || ''
}

function resolveCustomerContactName(customer: OwnerCustomer): string {
  return `${customer.contact_first_name || ''} ${customer.contact_last_name || ''}`.trim()
}

/** Resolve display contact and address from linked owner entities (single source of truth). */
export function resolveOwnerContact(input: ResolveOwnerContactInput): ResolvedOwnerContact {
  const empty: ResolvedOwnerContact = {
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_type: '',
  }

  if (input.customer) {
    empty.company_name = input.customer.company_name || ''
    empty.contact_name = resolveCustomerContactName(input.customer)
    empty.contact_email = input.customer.email || ''
    empty.contact_phone = input.customer.phone || ''
  } else if (input.lead) {
    empty.company_name = input.lead.company_name || ''
    empty.contact_name = resolveLeadContactName(input.lead)
    empty.contact_email = resolveLeadContactEmail(input.lead)
    empty.contact_phone = resolveLeadContactPhone(input.lead)
  }

  if (input.property) {
    if (input.property.site_contact_name?.trim()) {
      empty.contact_name = input.property.site_contact_name.trim()
    }
    if (input.property.site_contact_email?.trim()) {
      empty.contact_email = input.property.site_contact_email.trim()
    }
    if (input.property.site_contact_phone?.trim()) {
      empty.contact_phone = input.property.site_contact_phone.trim()
    }
    empty.property_address = input.property.address || input.property.name || ''
    empty.property_city = input.property.city || ''
    empty.property_state = input.property.state || ''
    empty.property_zip = input.property.zip_code || ''
    empty.property_type = input.property.property_type || ''
  } else if (input.leadProject) {
    empty.property_address = input.leadProject.address || ''
    empty.property_city = input.leadProject.city || ''
    empty.property_state = input.leadProject.state || ''
    empty.property_zip = input.leadProject.zip || ''
    empty.property_type = input.leadProject.type || ''
  }

  return empty
}

/** Whether an owner (customer or lead) is linked — contact should be read-only in forms. */
export function hasLinkedOwner(customerId?: string | null, leadId?: string | null): boolean {
  return Boolean(customerId?.trim() || leadId?.trim())
}

/** Format property site contacts JSON for display */
export function formatPropertyContactsDisplay(
  contacts: Array<{ first_name?: string; last_name?: string; name?: string }> | null | undefined
): string {
  if (!contacts?.length) return ''
  return contacts.map((c) => getContactDisplayName(c)).join(', ')
}
